<template>
  <footer class="sf-footer">
    <div class="sf-wrap">
      <div class="foot-mega">
        <div v-for="column in columns" :key="column.label" class="foot-sec">
          <h5>{{ column.label }}</h5>
          <ul>
            <li v-for="link in column.children" :key="`${column.label}-${link.label}`">
              <button
                v-if="link.onClick"
                type="button"
                class="foot-action"
                :disabled="link.disabled"
                @click="link.onClick"
              >
                {{ link.label }}
              </button>
              <a
                v-else-if="isExternalUrl(link.to)"
                :href="link.to"
                :target="link.target || '_blank'"
                rel="noopener noreferrer"
              >
                {{ link.label }}
              </a>
              <NuxtLink v-else :to="link.to" @click="handleLinkClick(link.to, $event)">
                {{ link.label }}
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>

      <div class="foot-lockup">
        <NuxtLink class="foot-logo" to="/">
          <span>Shark<b>Foto</b></span>
        </NuxtLink>
        <p class="foot-tag">Simple Creations with AI</p>
      </div>

      <div class="foot-bot">
        <span>© SharkFoto {{ new Date().getFullYear() }} · All rights reserved</span>
        <div class="links">
          <template v-for="item in policyLinks" :key="item.label">
            <a
              v-if="isExternalUrl(item.to)"
              :href="item.to"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ item.label }}
            </a>
            <NuxtLink v-else :to="item.to">
              {{ item.label }}
            </NuxtLink>
          </template>
        </div>
        <div class="socials">
          <a
            v-for="item in socialLinks"
            :key="item.label"
            :href="item.to"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="item.label"
          >
            <UIcon :name="item.icon" />
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { useStore } from "vuex";
import { computed, inject, ref } from "vue";
import {
  createProductIndexes,
  enabledModels,
  menuGroupsForArea,
  productForMenuItem,
  toInternalRoute,
} from "@/utils/aiCatalogData";

const store = useStore();
const token = computed(() => store.state.user.token);
const { openSignInModal, openSignUpModal } = useAuthModals();
const menuData = inject("menuData", ref([]));
const productList = inject("productList", ref([]));
const modelList = inject("modelList", ref([]));
const route = useRoute();

const freeTools = [
  { label: "Image Converter", to: "/free-tools/image/converter/" },
  { label: "Image Compressor", to: "/free-tools/image/compressor/" },
  { label: "Image Resizer", to: "/free-tools/image/resize-image/" },
  { label: "Image Flipper", to: "/free-tools/image/flip-image/" },
  { label: "Image Rotater", to: "/free-tools/image/rotate-image/" },
];

const resourceLinks = computed(() => [
  { label: "Sign In", onClick: openSignInModal, disabled: Boolean(token.value) },
  { label: "Sign Up", onClick: openSignUpModal, disabled: Boolean(token.value) },
  { label: "Plan & Pricing", to: "/pricing/" },
  { label: "Formats", to: "/format/" },
  { label: "Support", to: "/support/" },
  { label: "Blog", to: "https://sharkfoto.com/blog/" },
  { label: "Contact Us", to: "/company/contact-us/" },
  { label: "About Us", to: "/company/" },
]);

const policyLinks = [
  { label: "Privacy Policy", to: "/company/trust/privacy/" },
  { label: "Terms of Use", to: "/company/trust/terms/" },
  { label: "Refund Policy", to: "/company/trust/refund-policy/" },
  {
    label: "Cookie Policy",
    to: "/company/trust/privacy/#sources-of-information-and-tracking-technologies",
  },
];

const socialLinks = [
  { label: "Pinterest", icon: "mdi:pinterest", to: "https://www.pinterest.com/sharkfoto/" },
  { label: "Facebook", icon: "mdi:facebook", to: "https://www.facebook.com/SharkFoto-100253106125543" },
  { label: "Twitter / X", icon: "line-md:twitter-x", to: "https://twitter.com/Shark_Foto" },
  { label: "LinkedIn", icon: "mdi:linkedin", to: "https://www.linkedin.com/company/sharkfoto" },
];

function isExternalUrl(to) {
  return typeof to === "string" && /^https?:\/\//i.test(to);
}

function sortChildren(a, b) {
  const d = Number(a.pageIndex ?? 9999) - Number(b.pageIndex ?? 9999);
  if (d !== 0) return d;
  return String(a.label || "").localeCompare(String(b.label || ""));
}

function pushColumn(map, label, child) {
  if (!label) return;
  if (!map.has(label)) {
    map.set(label, { label, children: [] });
  }
  map.get(label).children.push(child);
}

const columns = computed(() => {
  const map = new Map();
  const productIndexes = createProductIndexes(productList.value);
  for (const group of menuGroupsForArea(menuData.value, "footer")) {
    if (String(group.title || "") === "AI Models") continue;
    for (const item of group.children || []) {
      const product = productForMenuItem(item, productIndexes);
      pushColumn(map, group.title, {
        label: product?.title || item.title,
        to: toInternalRoute(item.url),
        target: item?.target || undefined,
        pageIndex: item?.sortIndex,
      });
    }
  }

  for (const model of enabledModels(modelList.value)) {
    for (const group of model.groups || []) {
      pushColumn(map, group, {
        label: model.modelName || model.title,
        to: toInternalRoute(model.url),
        pageIndex: model.sortIndex,
      });
    }
  }

  const preferredOrder = [
    "Image AI",
    "Video AI",
    "Audio AI",
    "Image Models",
    "Video Models",
    "Audio Models",
  ];
  const dynamicColumns = [...map.values()]
    .map((column) => ({
      ...column,
      children: [...column.children].sort(sortChildren),
    }))
    .filter((column) => column.children.length)
    .sort((a, b) => {
      const ai = preferredOrder.indexOf(a.label);
      const bi = preferredOrder.indexOf(b.label);
      if (ai !== -1 || bi !== -1) {
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      }
      return a.label.localeCompare(b.label);
    });

  return [
    ...dynamicColumns,
    { label: "Free Tools", children: freeTools },
    { label: "Resource", children: resourceLinks.value },
  ];
});

function normalizePath(path) {
  if (!path) return "/";
  let normalized = path === "/" ? "/" : String(path).replace(/\/+$/, "");
  if (!normalized.startsWith("/")) normalized = `/${normalized}`;
  return normalized;
}

function handleLinkClick(to, event) {
  if (isExternalUrl(to)) return;
  const normalizedTargetPath = normalizePath(to);
  const normalizedCurrentPath = normalizePath(route.path);
  if (normalizedTargetPath === normalizedCurrentPath) {
    event?.preventDefault();
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }
}
</script>

<style scoped>
.sf-footer {
  --line: rgba(255, 255, 255, 0.09);
  --line-strong: rgba(255, 255, 255, 0.16);
  --tx: #f4f6fa;
  --tx-dim: rgba(244, 246, 250, 0.6);
  --tx-faint: rgba(244, 246, 250, 0.42);
  --brand: #8b5cf6;
  padding: 64px 0 30px;
  border-top: 1px solid var(--line);
  color: var(--tx);
  font-family: "Public Sans", system-ui, sans-serif;
}

.sf-wrap {
  max-width: 1340px;
  margin: 0 auto;
  padding: 0 28px;
}

.foot-mega {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 34px 22px;
  align-items: start;
  padding-bottom: 48px;
}

.foot-sec {
  min-width: 0;
}

.foot-sec h5 {
  margin-bottom: 15px;
  color: var(--tx);
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.foot-sec ul {
  display: grid;
  gap: 11px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.foot-sec a,
.foot-action {
  display: block;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--tx-dim);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  text-align: left;
  transition: 0.15s;
}

.foot-action:not(:disabled) {
  cursor: pointer;
}

.foot-sec a:hover,
.foot-action:not(:disabled):hover {
  color: var(--brand);
}

.foot-action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.foot-lockup {
  padding: 46px 0 32px;
  border-top: 1px solid var(--line);
  text-align: center;
}

.foot-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 11px;
}

.foot-logo span {
  color: #fff;
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.foot-logo b {
  color: var(--brand);
  font-weight: 700;
}

.foot-tag {
  margin-top: 12px;
  color: #c9b6ff;
  font-family: "Birthstone", cursive;
  font-size: 34px;
  line-height: 1;
}

.foot-bot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding-top: 26px;
  border-top: 1px solid var(--line);
  color: var(--tx-faint);
  font-size: 13px;
  font-weight: 500;
}

.foot-bot .links {
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
}

.foot-bot .links a {
  transition: 0.15s;
}

.foot-bot .links a:hover {
  color: var(--tx);
}

.socials {
  display: flex;
  gap: 10px;
}

.socials a {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 10px;
  color: var(--tx-dim);
  transition: 0.18s;
}

.socials a:hover {
  border-color: var(--line-strong);
  background: rgba(255, 255, 255, 0.04);
  color: var(--tx);
}

/* 移动/平板:grid + align-items:start 在列高差很大时(Image AI 11 条 vs Audio AI 2 条)
   会在矮列下留大片空白。改用 CSS 多列(masonry)按高度自动平衡、每列不拆分,消除空隙。 */
@media (max-width: 760px) {
  .foot-mega {
    display: block;
    column-count: 3;
    column-gap: 24px;
    padding-bottom: 40px;
  }
  .foot-sec {
    break-inside: avoid;
    -webkit-column-break-inside: avoid;
    margin-bottom: 28px;
  }

  .foot-bot {
    justify-content: center;
    text-align: center;
  }

  .foot-bot .links,
  .socials {
    justify-content: center;
  }
}

@media (max-width: 460px) {
  .foot-mega {
    column-count: 2;
  }
}
</style>
